export default function DownloadOrExportRecordingController(
  $uibModalInstance,
  $scope,
  $timeout,
  $rootScope,
  toaster,
  Recording,
  VimeoUser,
  YouTubeUser,
  $analytics,
  broadcast
) {
  'ngInject';

  this.hasVideo = broadcast.transcoder_profile !== 'audio';
  this.activeTab = this.hasVideo ? 'mp4' : 'm4a';
  this.unlock = {};

  this.allowKaltura = false;
  this.allowYoutube = false;
  this.allowVimeo = false;

  this.youtubeUsers = [];
  this.youtubeUserId = null;
  this.youtubeLicense = 'youtube';
  this.youtubePrivacy = 'private';

  this.vimeoUsers = [];
  this.vimeoUserId = null;

  this.allowKaltura = $rootScope.account.extra_features.kaltura;
  this.allowYoutube = !!$rootScope.account.feature_flags.youtube_export;
  this.allowVimeo = !!$rootScope.account.feature_flags.vimeo_export;

  if (this.allowYoutube) {
    this.youtubeUsers = YouTubeUser.query();
    this.youtubeUsers.$promise.then(() => {
      this.youtubeUserId = (_.first(this.youtubeUsers) || {}).id;
    });
  }

  if (this.allowVimeo) {
    this.vimeoUsers = VimeoUser.query();
    this.vimeoUsers.$promise.then(() => {
      this.vimeoUserId = (_.first(this.vimeoUsers) || {}).id;
    });
  }

  this.recording = {};
  this.$reloadRecordingTimeout = null;
  this.reloadRecording = () => {
    $timeout.cancel(this.$reloadRecordingTimeout);
    Recording.get(
      { id: broadcast.recording_id },
      (recording) => {
        // On completion of an export, make sure we re-lock
        _.each(['kaltura', 'vimeo', 'youtube'], (e) => {
          if (
            this.recording[`${e}_export_status`] !== 'ready' &&
            recording[`${e}_export_status`] == 'ready'
          ) {
            delete this.unlock[e];
          }
        });
        this.recording = recording;
        this.$reloadRecordingTimeout = $timeout(this.reloadRecording, 5000);
      },
      (error) => {
        console.error(error);
        toaster.pop('warning', 'Unable to fetch recording. Please try again.');
        this.cancel();
      }
    );
  };
  this.reloadRecording();
  $scope.$on('$destroy', () => $timeout.cancel(this.$reloadRecordingTimeout));

  this.cancel = () => {
    $uibModalInstance.dismiss('cancel');
  };

  this.canStart = () => {
    const processingStatuses = ['requesting', 'requested', 'awaiting_conversion'];
    const mp4DownloadInFlight = () =>
      _.contains(processingStatuses, this.recording.download_status) ||
      _.startsWith(this.recording.download_status, 'processing:');
    const m4aDownloadInFlight = () =>
      _.contains(processingStatuses, this.recording.audio_download_status) ||
      _.startsWith(this.recording.audio_download_status, 'processing:');
    const kalturaExportInFlight = () =>
      _.contains(processingStatuses, this.recording.kaltura_export_status) ||
      _.startsWith(this.recording.kaltura_export_status, 'processing:');
    const vimeoExportInFlight = () =>
      _.contains(processingStatuses, this.recording.vimeo_export_status) ||
      _.startsWith(this.recording.vimeo_export_status, 'processing:');
    const youtubeExportInFlight = () =>
      _.contains(processingStatuses, this.recording.youtube_export_status) ||
      _.startsWith(this.recording.youtube_export_status, 'processing:');
    const exportInFlight = () =>
      kalturaExportInFlight() || vimeoExportInFlight() || youtubeExportInFlight();

    if (this.activeTab === 'mp4') {
      return (
        (!this.recording.download_url || this.recording.download_status == 'expired') &&
        !mp4DownloadInFlight() &&
        !exportInFlight()
      );
    } else if (this.activeTab === 'm4a') {
      return (
        (!this.recording.audio_download_url || this.recording.audio_download_status == 'expired') &&
        !m4aDownloadInFlight()
      );
    } else if (this.activeTab === 'kaltura') {
      return !mp4DownloadInFlight() && !kalturaExportInFlight();
    } else if (this.activeTab === 'vimeo') {
      return !mp4DownloadInFlight() && !vimeoExportInFlight();
    } else if (this.activeTab === 'youtube') {
      return !mp4DownloadInFlight() && !youtubeExportInFlight();
    }
  };

  this.changeTab = (tabId) => {
    this.activeTab = tabId;
    this.unlock = {};
  };

  this.start = () => {
    // the debounce in angular is dubious; just double-check here that we can start, and get out if we can't.
    if (!this.canStart()) return;

    if (this.activeTab === 'mp4') {
      this.recording.download_status = 'requesting';
      Recording.prepareDownload(
        { id: broadcast.recording_id },
        (response) => {
          this.recording.download_status = 'requested';
          console.log('Submitted request to download mp4...', response);
          this.reloadRecording();
        },
        (error) => {
          this.recording.download_status = 'failed';
          console.error(error);
          toaster.pop('warning', 'Unable to prepare mp4 download. Please try again.');
          $analytics.eventTrack('event-download', { category: 'error' });
        }
      );
    } else if (this.activeTab === 'm4a') {
      this.recording.audio_download_status = 'requesting';
      Recording.prepareDownload(
        { id: broadcast.recording_id, audio_only: true },
        (response) => {
          this.recording.audio_download_status = 'requested';
          console.log('Submitted request to m4a download...', response);
          this.reloadRecording();
        },
        (error) => {
          this.recording.audio_download_status = 'failed';
          console.error(error);
          toaster.pop('warning', 'Unable to prepare m4a download. Please try again.');
          $analytics.eventTrack('event-download-audio', { category: 'error' });
        }
      );
    } else if (this.activeTab === 'kaltura') {
      this.recording.kaltura_export_status = 'requesting';
      Recording.startKalturaExport(
        { id: broadcast.recording_id },
        (response) => {
          this.recording.kaltura_export_status = 'requested';
          console.log('Submitted request to export to kaltura...', response);
          this.reloadRecording();
        },
        (error) => {
          this.recording.kaltura_export_status = 'failed';
          console.error(error);
          toaster.pop('warning', 'Unable to export video. Please try again.');
          $analytics.eventTrack('event-kaltura-export', { category: 'error' });
        }
      );
    } else if (this.activeTab === 'vimeo') {
      // This isn't documented, but Vimeo limits the name to 128 characters (PLATFORM-6277)
      const videoInfo = {
        name: broadcast.name.substr(0, 128),
        description: broadcast.description,
      };
      this.recording.vimeo_export_status = 'requesting';
      Recording.startVimeoExport(
        { id: broadcast.recording_id, vimeo_user_id: this.vimeoUserId, video_info: videoInfo },
        (response) => {
          this.recording.vimeo_export_status = 'requested';
          console.log('Submitted request to export to vimeo...', response);
          this.reloadRecording();
        },
        (error) => {
          this.recording.vimeo_export_status = 'failed';
          console.error(error);
          toaster.pop('warning', 'Unable to export video. Please try again.');
          $analytics.eventTrack('event-vimeo-export', { category: 'error' });
        }
      );
    } else if (this.activeTab === 'youtube') {
      // https://developers.google.com/youtube/v3/docs/videos
      //
      // title (string): The video's title. The property value has a maximum length of 100 characters
      // and may contain all valid UTF-8 characters except < and >.
      //
      // description (string): The video's description. The property value has a maximum length of
      // 5000 bytes and may contain all valid UTF-8 characters except < and >.
      const videoInfo = {
        snippet: {
          title: broadcast.name.replace(/</g, '[').replace(/>/g, ']').substr(0, 100),
          description: broadcast.description.replace(/</g, '[').replace(/>/g, ']').substr(0, 5000),
        },
        status: {
          privacyStatus: this.youtubePrivacy,
          license: this.youtubeLicense,
        },
      };
      this.recording.youtube_export_status = 'requesting';
      Recording.startYoutubeExport(
        { id: broadcast.recording_id, youtube_user_id: this.youtubeUserId, video_info: videoInfo },
        (response) => {
          this.recording.youtube_export_status = 'requested';
          console.log('Submitted request to export to youtube...', response);
          this.reloadRecording();
        },
        (error) => {
          this.recording.youtube_export_status = 'failed';
          console.error(error);
          toaster.pop('warning', 'Unable to export video. Please try again.');
          $analytics.eventTrack('event-youtube-export', { category: 'error' });
        }
      );
    }
  };

  this.status = () => {
    let s = null;
    if (this.activeTab === 'mp4') {
      s = this.recording.download_status;
    } else if (this.activeTab === 'm4a') {
      s = this.recording.audio_download_status;
    } else if (this.activeTab === 'kaltura') {
      s = this.recording.kaltura_export_status;
    } else if (this.activeTab === 'vimeo') {
      s = this.recording.vimeo_export_status;
    } else if (this.activeTab === 'youtube') {
      s = this.recording.youtube_export_status;
    }
    if (_.startsWith(s, 'processing:')) {
      s = 'processing';
    }

    return s;
  };

  this.statusPercent = () => {
    let p = 0;
    if (this.activeTab === 'mp4' || this.activeTab === 'm4a') {
      let s = null;
      if (this.activeTab === 'mp4') {
        s = this.recording.download_status;
      } else if (this.activeTab === 'm4a') {
        s = this.recording.audio_download_status;
      }
      if (s === 'ready') {
        p = 100;
      } else if (_.startsWith(s, 'processing:')) {
        p = parseInt(s.replace('processing:', ''), 10) || 0;
      }
    } else {
      // Compensate for fact that download-preparation is only first half of the battle.
      // Now that we have an MP4 in S3, we need to get it over to the social provider.
      let s = null;
      if (this.activeTab === 'kaltura') {
        s = this.recording.kaltura_export_status;
      } else if (this.activeTab === 'vimeo') {
        s = this.recording.vimeo_export_status;
      } else if (this.activeTab === 'youtube') {
        s = this.recording.youtube_export_status;
      }
      if (s === 'ready') {
        p = 100;
      } else if (_.contains(['awaiting_conversion', 'requested'], s)) {
        if (this.recording.download_status === 'ready') {
          p = 50;
        } else if (_.startsWith(this.recording.download_status, 'processing:')) {
          p = Math.round(
            (parseInt(this.recording.download_status.replace('processing:', ''), 10) || 0) / 2
          );
        }
      } else if (_.startsWith(s, 'processing:')) {
        p = 50 + Math.round((parseInt(s.replace('processing:', ''), 10) || 0) / 2);
      }
    }

    return p;
  };
}



// WEBPACK FOOTER //
// ./app/pages/events/modal-download-or-export-recording.js